import { useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router";
import { districtManifest, worldStreets } from "@townsy/world";
import { useTownSession } from "../../app/TownSessionContext";
import type { MinimapSnapshot, TownSession } from "../../app/townSession";
import { districtName, streetOfAddress } from "../../lib/directoryFilters";
import { propertyDisplayName } from "../../lib/propertyLabels";
import { useTownStore, type TownStore } from "../../state/store";
import { MINIMAP_COLORS as C, drawDistrictLabels, drawMinimapDynamic, drawMinimapStatic, drawRoadLinks, minimapLabelFont as labelFont } from "./minimapDraw";
import {
  browserLocalStorage,
  describePosition,
  districtRoads,
  fitMinimap,
  hitTestMinimap,
  layoutDistrictLabels,
  layoutRoadLinks,
  layoutStreetLabels,
  minimapCanvasHeight,
  minimapCaption,
  minimapAriaLabel,
  minimapDistrict,
  minimapPlots,
  minimapWorld,
  readMinimapView,
  roadLinkHover,
  roadsFrom,
  writeMinimapView,
  type MinimapHit,
  type MinimapViewKind,
} from "./minimapModel";

/**
 * The small town map (ADR-0008 #3): a canvas overlay in a screen corner with the streets and their
 * names, every plot (owned / available / Townsy), you and your heading, the car you drive and the
 * other cars. A click on a plot selects it (the same `/place/:id` flow as the directory); a click on
 * a street name opens the directory filtered to that street. It never takes keyboard focus (A09):
 * the canvas is not focusable and mouse presses do not move focus, so WASD keeps walking or driving.
 * Hidden inside buildings, before the 3D view is ready and in the 2D fallback (never mounted there).
 *
 * World v5 (ADR-0041 decision 9): "Here" shows the district the visitor is standing in (the town store's `districtId`,
 * which the session keeps up to date from the visitor's position, ADR-0042 decision 1); "Town" fits all three districts
 * with their names and "you are here". ADR-0042 decision 2: the districts are joined by road, so Here draws each road
 * to another district running off the map's edge ("To Riverside") and Town draws the roads across the district lines.
 * Plots are clickable in both views; the choice is remembered.
 */
export const selectMinimapVisible = (s: Pick<TownStore, "ui" | "room">): boolean =>
  s.ui.minimapOpen && s.ui.sceneStatus === "ready" && s.room.kind === "street";

/** Street-name size: 10 px on the desktop map, 9 px on the narrower phone map. */
const fontPxFor = (width: number) => (width < 280 ? 9 : 10);
const REFRESH_MS = 100;
const CAPTION_EVERY = 5; // frames

export function Minimap() {
  const session = useTownSession();
  const visible = useTownStore(selectMinimapVisible);
  if (!session || !visible) return null;
  return <MinimapCanvas session={session} />;
}

let measureCtx: CanvasRenderingContext2D | null | undefined;
function measureLabel(text: string, px: number): number {
  if (measureCtx === undefined) {
    try {
      measureCtx = document.createElement("canvas").getContext("2d");
    } catch {
      measureCtx = null;
    }
  }
  if (!measureCtx) return text.length * px * 0.56;
  measureCtx.font = labelFont(px);
  return measureCtx.measureText(text).width;
}

/** The viewer's last Here / Town choice survives leaving a building (the map unmounts inside) and reloads. */
let rememberedView: MinimapViewKind | null = null;

function MinimapCanvas({ session }: { session: TownSession }) {
  const navigate = useNavigate();
  const properties = useTownStore((s) => s.properties);
  const selected = useTownStore((s) => s.ui.selectedPropertyId);
  const setOpen = useTownStore((s) => s.setMinimapOpen);
  const openDirectory = useTownStore((s) => s.openDirectory);
  const currentDistrictId = useTownStore((s) => s.districtId);
  const [viewKind, setViewKind] = useState<MinimapViewKind>(() => (rememberedView ??= readMinimapView(browserLocalStorage)));
  const town = viewKind === "town";
  const manifest = session.manifest;
  const district = minimapDistrict(manifest, currentDistrictId);
  const slice = useMemo(() => districtManifest(manifest, district.id), [manifest, district.id]);
  const world = useMemo(() => minimapWorld(viewKind, manifest, district.id), [viewKind, manifest, district.id]);
  const sectionRef = useRef<HTMLElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(320);
  const height = minimapCanvasHeight(world, width);
  const view = useMemo(() => fitMinimap(world, width, height), [world, width, height]);
  // The current district's plots and streets (the caption, street names); the Town view draws and hit-tests every plot.
  const herePlots = useMemo(() => minimapPlots(manifest, district.id), [manifest, district.id]);
  const plots = useMemo(() => (town ? minimapPlots(manifest) : herePlots), [town, manifest, herePlots]);
  const streets = useMemo(() => worldStreets(manifest, district.id), [manifest, district.id]);
  // Only streets that have places are clickable (Orchard Road's homes are addressed on their lanes).
  const listable = useMemo(() => new Set(herePlots.map((p) => streetOfAddress(p.address))), [herePlots]);
  const fontPx = fontPxFor(width);
  const labels = useMemo(() => (town ? [] : layoutStreetLabels(view, streets, (t) => measureLabel(t, fontPx), fontPx)), [town, view, streets, fontPx]);
  const clickableLabels = useMemo(() => labels.filter((l) => listable.has(l.name)), [labels, listable]);
  // ADR-0042: the roads across the district lines (from the manifest's road surfaces and named streets).
  const roads = useMemo(() => districtRoads(manifest, (id) => worldStreets(manifest, id)), [manifest]);
  const links = useMemo(() => (town ? [] : layoutRoadLinks(view, district, roads, districtName, (t) => measureLabel(t, fontPx), fontPx)), [town, view, district, roads, fontPx]);
  const districtLabels = useMemo(
    () => (town ? layoutDistrictLabels(view, manifest.districts, district.id, (t) => measureLabel(t, fontPx + 1), fontPx) : []),
    [town, view, manifest, district.id, fontPx],
  );
  const [where, setWhere] = useState("Finding you…");
  const [hover, setHover] = useState<{ text: string; clickable: boolean } | null>(null);

  // The CSS decides the width (wider on desktop, narrower on phones); the height follows the town's shape.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const apply = () => {
      const w = Math.round(el.clientWidth);
      if (w > 40) setWidth((prev) => (prev === w ? prev : w));
    };
    apply();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Toasts and the right-hand panel keep clear of the map on desktop (`--minimap-space`, see global.css).
  useLayoutEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const root = document.documentElement;
    const apply = () => root.style.setProperty("--minimap-space", `${Math.ceil(el.offsetHeight)}px`);
    apply();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(apply);
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      root.style.removeProperty("--minimap-space");
    };
  }, []);

  // Streets, plots and names change rarely: draw them once into a layer, then only the moving things per frame.
  const layer = useMemo(() => {
    if (typeof document === "undefined") return null;
    const dpr = Math.min(Math.max(window.devicePixelRatio || 1, 1), 2);
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(width * dpr));
    c.height = Math.max(1, Math.round(height * dpr));
    const g = c.getContext("2d");
    if (!g) return null;
    g.scale(dpr, dpr);
    // Here: the district's own surfaces (its slice); Town: every district's.
    drawMinimapStatic(g, view, town ? manifest : slice, plots, properties, selected, labels, fontPx);
    if (town) drawDistrictLabels(g, districtLabels, fontPx, view, roads);
    else drawRoadLinks(g, view, links, fontPx);
    return { canvas: c, dpr };
  }, [view, town, manifest, slice, plots, properties, selected, labels, links, roads, districtLabels, width, height, fontPx]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !layer) return;
    canvas.width = layer.canvas.width;
    canvas.height = layer.canvas.height;
    const g = canvas.getContext("2d");
    if (!g) return;
    let frame = 0;
    let lastWhere = "";
    const draw = () => {
      let snap: MinimapSnapshot;
      try {
        snap = session.minimapSnapshot();
      } catch {
        return;
      }
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.clearRect(0, 0, canvas.width, canvas.height);
      g.drawImage(layer.canvas, 0, 0);
      g.setTransform(layer.dpr, 0, 0, layer.dpr, 0, 0);
      // The whole town is too small for traffic: only you (and the car you drive) there.
      drawMinimapDynamic(g, view, town ? { ...snap, cars: [] } : snap);
      if (frame++ % CAPTION_EVERY === 0) {
        const text = describePosition(snap.local, herePlots, streets);
        if (text !== lastWhere) {
          lastWhere = text;
          setWhere(text);
        }
      }
    };
    draw();
    const t = setInterval(draw, REFRESH_MS);
    return () => clearInterval(t);
  }, [session, layer, view, town, herePlots, streets]);

  const chooseView = (next: MinimapViewKind) => {
    rememberedView = next;
    writeMinimapView(browserLocalStorage, next);
    setViewKind(next);
    setHover(null);
  };
  const pointOf = (e: MouseEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * width) / Math.max(r.width, 1), y: ((e.clientY - r.top) * height) / Math.max(r.height, 1) };
  };
  const hitAt = (e: MouseEvent<HTMLCanvasElement>): MinimapHit => hitTestMinimap(view, clickableLabels, plots, pointOf(e), 3, links);
  const describeHit = (hit: MinimapHit): { text: string; clickable: boolean } | null => {
    if (!hit) return null;
    if (hit.kind === "street") return { text: `${hit.name}: list its places`, clickable: true };
    if (hit.kind === "road") return { text: roadLinkHover(hit), clickable: false };
    const p = properties[hit.id];
    const plot = plots.find((x) => x.id === hit.id);
    const name = p ? propertyDisplayName(p) : "Place";
    const elsewhere = plot && plot.districtId !== district.id ? `, ${districtName(plot.districtId)}` : "";
    return { text: `${name}, ${plot?.address ?? hit.id}${elsewhere}: details`, clickable: true };
  };
  const caption = minimapCaption(where, district.name);
  const roadsTo = [...new Set(roadsFrom(roads, district.id).map((r) => districtName(r.toDistrictId)))];

  return (
    <section ref={sectionRef} className="minimap" aria-label="Town map" data-testid="minimap" data-view={viewKind} data-district={district.id}>
      <div ref={wrapRef} className="minimap-frame">
        <canvas
          ref={canvasRef}
          className="minimap-canvas"
          style={{ width: "100%", height }}
          role="img"
          aria-label={minimapAriaLabel({
            town,
            districtNames: manifest.districts.map((d) => d.name),
            here: district.name,
            roadsTo,
            joinedByRoad: roads.length > 0,
            caption,
          })}
          data-testid="minimap-canvas"
          // Never take focus from the town (A09): a press on the map keeps keyboard walking/driving working.
          onMouseDown={(e) => e.preventDefault()}
          onMouseMove={(e) => {
            const next = describeHit(hitAt(e));
            if (next?.text !== hover?.text || next?.clickable !== hover?.clickable) setHover(next);
          }}
          onMouseLeave={() => setHover(null)}
          onClick={(e) => {
            const hit = hitAt(e);
            if (!hit) return;
            if (hit.kind === "plot") navigate(`/place/${hit.id}`);
            else if (hit.kind === "street") openDirectory(hit.name);
            // A road to another district only explains itself (hover): walking or driving along it is how you get there.
          }}
          data-hover={hover?.clickable ? "true" : undefined}
        />
      </div>
      <div className="minimap-footer">
        <div className="segmented minimap-views" role="group" aria-label="Map view">
          <button
            type="button"
            className="btn btn-sm"
            aria-pressed={!town}
            title={`${district.name}, where you are: its streets and places`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => chooseView("here")}
            data-testid="minimap-view-here"
          >
            Here
          </button>
          <button
            type="button"
            className="btn btn-sm"
            aria-pressed={town}
            title="The whole town: every district"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => chooseView("town")}
            data-testid="minimap-view-town"
          >
            Town
          </button>
        </div>
        <span className="minimap-caption" data-testid="minimap-caption">
          {hover?.text ?? caption}
        </span>
        <button
          type="button"
          className="btn btn-ghost btn-sm minimap-close"
          aria-label="Hide the map"
          title="Hide the map (the Map button brings it back)"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setOpen(false)}
        >
          ×
        </button>
      </div>
      <div className="minimap-legend" aria-hidden="true">
        <span>
          <i style={{ background: C.owned }} /> Owned
        </span>
        <span>
          <i style={{ background: C.available, borderColor: C.availableEdge }} /> For sale
        </span>
        <span>
          <i style={{ background: C.civic }} /> Townsy
        </span>
        <span>
          <i className="minimap-legend-me" /> You
        </span>
      </div>
    </section>
  );
}
